import { subMinutes } from 'date-fns';
import { type Event, type EventParticipantRsvp, type Pagination, ProgramAbstractEvent, createEvent } from 'models';
import { useInfiniteQuery } from 'query';
import type { PaginationData } from 'common/services';
import { getNodeEvents } from '../services';
import type { EventTimelineType } from '../types';
import { getNodeEventsInfiniteQueryKey } from './keys';

export type NodeEventsListFilter = {
  rsvpStatus?: EventParticipantRsvp[];
  type?: EventTimelineType;
  userId: string;
};

type EventsQueryParams = {
  filter?: NodeEventsListFilter;
  options?: { enabled?: boolean };
  pagination: Omit<Pagination, 'position'>;
};

type NodeEventsPage = {
  time?: string;
} & PaginationData<Event>;

type NodeEventsPageParameter = {
  skip: number;
  time?: string;
};

export const useNodeEventsInfiniteQuery = ({ filter, options, pagination }: EventsQueryParams) =>
  useInfiniteQuery<NodeEventsPage, NodeEventsPageParameter>({
    async queryFn({ pageParam }) {
      const initialTime = subMinutes(new Date(), ProgramAbstractEvent.leaveOffset).toISOString();
      const firstPageTime = pageParam.time ?? initialTime;

      const { data: nodeEvents } = await getNodeEvents({
        config: {
          params: {
            $limit: pagination.pageSize,
            $skip: pageParam.skip,
            $sort: filter?.type === 'upcoming' ? { startTime: '1' } : { startTime: '-1' },
            endTime: filter?.type === 'upcoming' ? { $gte: firstPageTime } : { $lte: firstPageTime },
            participants: {
              $elemMatch: {
                ref: filter?.userId ? { $in: [filter.userId] } : undefined,
                rsvp: filter?.rsvpStatus ? { $in: filter.rsvpStatus } : undefined,
              },
            },
          },
        },
      });

      return {
        data: nodeEvents.data.map((event) => createEvent(event)),
        limit: nodeEvents.limit,
        skip: nodeEvents.skip,
        time: firstPageTime,
        total: nodeEvents.total,
      };
    },
    initialPageParam: { skip: 0, time: undefined },
    getNextPageParam: (lastPage) =>
      lastPage.skip + lastPage.data.length < lastPage.total
        ? { skip: lastPage.skip + lastPage.limit, time: lastPage.time }
        : undefined,
    queryKey: getNodeEventsInfiniteQueryKey({
      filter,
      pagination,
    }),
    refetchOnMount: 'always',
    throwOnError: true,
    ...options,
  });
